import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { tokens } from "../../../theme";
import { useTheme } from "@mui/material";
import AddMemoDatePicker from './AddMemoDatePicker';
import Axios from 'axios';
import { getAuthHeader } from '../../../utils/authToken';
import MainMemoOptionsToolbar from '../../weekly/mainMemo/MainMemoOptionsToolbar';
import stripHtml from '../../../utils/stripHtml';


export default function AddMemoAccordion({
    student_uuid, 
    setStudentUpdated,
    setSuccessAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertOpen,
    setErrorAlertMessage, 
    socket = null,
    socketConnected = false,
    from_weekly = false,
    getStudentName,
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [reminderDate, setReminderDate] = React.useState(null);
    const [bookmark, setBookmark] = React.useState(false);

    // Rich-text memo editor (contentEditable body + reused inline toolbar).
    const contentRef = React.useRef(null);
    const toolbarRef = React.useRef(null);
    // Only mount the formatting toolbar once the editor is engaged.
    const [isEditingContent, setIsEditingContent] = React.useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const contentHtml = contentRef.current?.innerHTML || "";
        if (stripHtml(contentHtml).trim() === "") {
            return;
        }

        await Axios.post(`${process.env.REACT_APP_URL}/api/v1/students/${student_uuid}/memos`, {
            title: data.get('title'),
            content: contentHtml,
            reminder: reminderDate ? reminderDate.toDate() : null,
            bookmark: bookmark,
        }, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then((response) => {
            if (!from_weekly) {
                setStudentUpdated(true);
                setSuccessAlertOpen(true);
                setSuccessAlertMessage(response.data.message);
            } else if (from_weekly && socketConnected) {
                // from weekly, so we need to emit to server (using socketio)
                socket.emit("put.StudentUpdate", {
                    student_uuid: student_uuid,
                    message: " created " + getStudentName() + "'s memo",
                });
            }
            // Clear the editor for the next memo.
            if (contentRef.current) contentRef.current.innerHTML = "";
            setIsEditingContent(false);
        }).catch((error) => {
            setErrorAlertOpen(true);
            setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
        });
    };

    return (
        <div style={{
            width: "100%",
            marginBottom: "1rem",
        }}>
            <Accordion
                style={{
                    backgroundColor: colors.primary[750],
                }}
                component="div"
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <Typography variant='h6'>Add Memo</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box
                        component="form"
                        sx={{
                            '& > :not(style)': { m: 1, width: '25ch' },
                            display: "flex",
                            flexDirection: "column",
                        }}
                        noValidate
                        autoComplete="off"
                        onSubmit={handleSubmit}
                    >
                        <Box sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            minWidth: "52rem",
                        }}>
                            <TextField
                                id="filled-basic"
                                label="Title"
                                variant="filled"
                                name='title'
                                sx={{
                                    flex: "1",
                                    maxWidth: "20rem",
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: colors.primary[600],
                                        ":focus": {
                                            backgroundColor: 'white',
                                        },
                                    },
                                }}
                            />
                            <AddMemoDatePicker reminderDate={reminderDate} setReminderDate={setReminderDate} />
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={bookmark}
                                        onChange={(e) => setBookmark(e.target.checked)}
                                        icon={<BookmarkBorderIcon />}
                                        checkedIcon={<BookmarkIcon />}
                                        sx={{
                                            color: colors.grey[400],
                                            '&.Mui-checked': {
                                                color: '#ed6c02',
                                            },
                                        }}
                                    />
                                }
                                label="Bookmark"
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        color: colors.grey[100],
                                    },
                                }}
                            />
                        </Box>
                        <Box
                            sx={{ minWidth: "52rem", width: "52rem" }}
                            // Hide the toolbar once focus leaves the editor+toolbar region.
                            // If focus is moving to a control still inside this wrapper
                            // (a toolbar button/select), keep the toolbar visible.
                            onBlur={(e) => {
                                if (!e.currentTarget.contains(e.relatedTarget)) {
                                    setIsEditingContent(false);
                                }
                            }}
                        >
                            {isEditingContent && (
                                <Box
                                    // Keep the editor focused (and its selection intact) when
                                    // clicking toolbar controls, so the toolbar doesn't blur-hide
                                    // — including in Safari/Firefox where buttons don't take focus
                                    // on click. Native controls still need mousedown.
                                    onMouseDown={(e) => {
                                        const tag = e.target.tagName;
                                        if (tag !== "SELECT" && tag !== "OPTION" && tag !== "INPUT") {
                                            e.preventDefault();
                                        }
                                    }}
                                >
                                    <MainMemoOptionsToolbar
                                        toolbarRef={toolbarRef}
                                        colors={colors}
                                        isMainMemo={true}
                                        inline={true}
                                    />
                                </Box>
                            )}
                            <Box
                                component="div"
                                contentEditable
                                suppressContentEditableWarning
                                ref={contentRef}
                                onFocus={() => setIsEditingContent(true)}
                                data-placeholder="Content"
                                sx={{
                                    minHeight: "160px",
                                    width: "100%",
                                    border: "1px solid #ccc",
                                    borderTop: isEditingContent ? "none" : "1px solid #ccc",
                                    padding: "8px",
                                    whiteSpace: "pre-wrap",
                                    overflow: "auto",
                                    textAlign: "left",
                                    backgroundColor: colors.primary[600],
                                    "&:focus": { outline: "2px solid #599DD5" },
                                    "&:empty:before": {
                                        content: "attr(data-placeholder)",
                                        color: "#999",
                                    },
                                }}
                            />
                        </Box>
                        <Button
                            type="submit"
                            variant="contained"
                            style={{
                                backgroundColor: colors.primary[300],
                                color: "white",
                                float: "right",
                                marginTop: "0.5rem",
                                marginBottom: "1rem",
                                width: "10rem",
                            }}
                        >
                            Add Memo
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </div>
    )
}